// Attachment upload client. POST multipart to /api/attachments, track simple
// per-file state for chip rendering in the composers.

import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * File drag-and-drop for a composer, with an UNSTICKABLE veil.
 *
 * No dragenter/dragleave counting — that leaks whenever a child handler stops
 * propagation, the drag is cancelled with Esc, or the pointer leaves the
 * window, leaving the veil permanently stuck over the composer. Instead the
 * veil is kept alive by the dragover stream (browsers re-fire dragover at
 * least every ~350ms while a drag hovers) and self-expires when it stops.
 */
export function useFileDrop(onFiles: (files: File[]) => void) {
  const [dragging, setDragging] = useState(false);
  const expireTimer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (expireTimer.current) window.clearTimeout(expireTimer.current);
    },
    [],
  );

  const rootProps = {
    onDragOver: (e: React.DragEvent) => {
      if (!Array.from(e.dataTransfer.types).includes('Files')) return;
      e.preventDefault();
      setDragging(true);
      if (expireTimer.current) window.clearTimeout(expireTimer.current);
      // Comfortably beyond the spec's 350ms dragover heartbeat.
      expireTimer.current = window.setTimeout(() => setDragging(false), 800);
    },
    onDrop: (e: React.DragEvent) => {
      if (expireTimer.current) window.clearTimeout(expireTimer.current);
      setDragging(false);
      if (!e.dataTransfer.files?.length) return;
      e.preventDefault();
      onFiles(Array.from(e.dataTransfer.files));
    },
  };

  return { dragging, rootProps };
}

export interface UploadedAttachment {
  id: number;
  filename: string;
  mimeType: string;
  sizeBytes: number;
}

export interface PendingUpload {
  /** Local-only id while in flight. */
  localId: string;
  filename: string;
  sizeBytes: number;
  status: 'uploading' | 'done' | 'error';
  error?: string;
  /** Set when status==='done'. */
  uploaded?: UploadedAttachment;
}

export const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;
/** Server rejects sends with more than this many attachments — enforce at the
 *  chip level so the failure is visible per-file, not at send time. */
export const MAX_ATTACHMENT_COUNT = 10;

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Hook managing a composer's attachment list. */
/** Content signature for duplicate detection: size + filename with the
 *  browser's download-copy suffix stripped ("June… 2.pdf" ≡ "June….pdf").
 *  Drafts restore their chips, so re-picking the same file from Downloads is
 *  the most common way to double an attachment and burn the 10-file cap. */
function fileSig(filename: string, sizeBytes: number): string {
  const base = filename.replace(/ \d+(\.[A-Za-z0-9]+)$/, '$1').toLowerCase();
  return `${base}:${sizeBytes}`;
}

export function useAttachmentUploads() {
  const [uploads, setUploads] = useState<PendingUpload[]>([]);
  // Live count + signatures for the cap/dupe checks — addFiles has an empty
  // dep array, so reading `uploads` inside it would be a stale closure.
  const countRef = useRef(0);
  countRef.current = uploads.filter((u) => u.status !== 'error').length;
  const sigsRef = useRef<Set<string>>(new Set());
  sigsRef.current = new Set(
    uploads.filter((u) => u.status !== 'error').map((u) => fileSig(u.filename, u.sizeBytes)),
  );

  const addFiles = useCallback((files: FileList | File[]) => {
    let liveCount = countRef.current;
    const liveSigs = new Set(sigsRef.current);
    for (const file of Array.from(files)) {
      const localId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      // Already attached (restored from the draft, or picked twice) — the
      // chip is in the list; silently skip instead of doubling it.
      if (liveSigs.has(fileSig(file.name, file.size))) continue;
      if (liveCount >= MAX_ATTACHMENT_COUNT) {
        setUploads((prev) => [
          ...prev,
          {
            localId,
            filename: file.name,
            sizeBytes: file.size,
            status: 'error',
            error: `Limit is ${MAX_ATTACHMENT_COUNT} attachments per email`,
          },
        ]);
        continue;
      }
      if (file.size > MAX_ATTACHMENT_BYTES) {
        setUploads((prev) => [
          ...prev,
          {
            localId,
            filename: file.name,
            sizeBytes: file.size,
            status: 'error',
            error: `Too large (max ${formatBytes(MAX_ATTACHMENT_BYTES)})`,
          },
        ]);
        continue;
      }
      liveCount += 1;
      liveSigs.add(fileSig(file.name, file.size));
      setUploads((prev) => [
        ...prev,
        { localId, filename: file.name, sizeBytes: file.size, status: 'uploading' },
      ]);
      const form = new FormData();
      form.append('file', file, file.name);
      fetch('/api/attachments', { method: 'POST', credentials: 'include', body: form })
        .then(async (r) => {
          if (!r.ok) {
            const j = await r.json().catch(() => ({}));
            throw new Error(j.error || `HTTP ${r.status}`);
          }
          const j = (await r.json()) as { attachments: UploadedAttachment[] };
          const uploaded = j.attachments[0];
          setUploads((prev) =>
            prev.map((u) =>
              u.localId === localId ? { ...u, status: 'done' as const, uploaded } : u,
            ),
          );
        })
        .catch((err) => {
          setUploads((prev) =>
            prev.map((u) =>
              u.localId === localId
                ? { ...u, status: 'error' as const, error: err instanceof Error ? err.message : 'Upload failed' }
                : u,
            ),
          );
        });
    }
  }, []);

  const remove = useCallback((localId: string) => {
    setUploads((prev) => {
      const target = prev.find((u) => u.localId === localId);
      // Best-effort server-side delete for completed uploads.
      if (target?.uploaded) {
        void fetch(`/api/attachments/${target.uploaded.id}`, {
          method: 'DELETE',
          credentials: 'include',
        }).catch(() => {});
      }
      return prev.filter((u) => u.localId !== localId);
    });
  }, []);

  const clear = useCallback(() => setUploads([]), []);

  /** Discard the whole list AND delete the completed uploads server-side —
   *  used by "Discard draft" so orphaned files don't pile up. */
  const discardAll = useCallback(() => {
    setUploads((prev) => {
      for (const u of prev) {
        if (u.uploaded) {
          void fetch(`/api/attachments/${u.uploaded.id}`, {
            method: 'DELETE',
            credentials: 'include',
          }).catch(() => {});
        }
      }
      return [];
    });
  }, []);

  /** Re-populate the list (undo-send restore). Only completed uploads make
   *  sense to bring back — their server rows were un-consumed by the cancel. */
  const restore = useCallback((items: PendingUpload[]) => {
    setUploads(items.filter((u) => u.status === 'done' && u.uploaded));
  }, []);

  const readyIds = uploads
    .filter((u) => u.status === 'done' && u.uploaded)
    .map((u) => u.uploaded!.id);
  const anyUploading = uploads.some((u) => u.status === 'uploading');

  return { uploads, addFiles, remove, clear, discardAll, restore, readyIds, anyUploading };
}
